import clsx from "clsx";
import { ChevronRight, Clock, FileText, Fuel, Mountain, Swords, Wrench } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import type { Logistics, LogisticsAssumptions, LogisticsMaintenance, MaintenancePhase, MaintenancePosture, MaterielFamily } from "../../api/types";
import { designateSoon } from "../../components/attention";
import { Callout } from "../../components/Callout";
import { Collapsible } from "../../components/Collapsible";
import { EmptyState, Spinner } from "../../components/EmptyState";
import { PanelGroup, PanelRow, StatTile } from "../../components/panel";
import { Motion } from "../../components/presence";
import { Button, FloatingPanel } from "../../components/ui";
import { VIEW_META } from "../../components/viewMeta";
import { Waiting } from "../../components/Waiting";
import { HelpButton } from "../../help/HelpButton";
import { unitDesignation } from "../../i18n/designation";
import { formatNumber, k, t } from "../../i18n/i18n";
import {
  bandText,
  CAPOPS_FULL,
  colorTone,
  dtoColor,
  DTO_FULL,
  dtoReading,
  expectedText,
  familyLabel,
  limitText,
  percentText,
} from "../../maintenance";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { ZoneWaitCallout } from "../../map/zones/ZoneWaitCallout";
import { useUi } from "../../state/ui";
import { OpenMissionDataButton } from "../openMissionData";
import { AssumptionsBadge, customOf, ResetAssumptions, Slider, type SliderSpec, useSaveAssumptions } from "../logistics/assumptions";
import { mobilityLabel, SituationLine, useLogistics } from "../logistics/LogisticsPanel";
import { CapopsBadge, DtoBadge, DtoCurve, DtoStatusIcon, initialFamily, LegLines } from "./DtoCurve";

/** Maintenance S4 (vue « Maintenance », chantier « DTO / CAPOPS », révision « DTO réaliste » du 02/11/2026) : la DTO
 *  (disponibilité technique opérationnelle) par famille de matériel, de T0 à T3, jugée contre la DTO attendue de chaque
 *  temps (trajectoire réglable, jamais un seuil fixe : une DTO au-dessus n'est jamais une alerte), et la CAPOPS des
 *  unités (maillon le plus faible du personnel, des matériels et des munitions, aux couleurs de l'iPad). Indicateurs
 *  de nature différente du ravitaillement (vue Logistique, UE, UF, drones), d'où une vue à part, qui lit pourtant la
 *  même réponse du serveur (`useLogistics` : même cache, même invalidation). « Ouvrir la maintenance » d'un constat
 *  de cohérence ou d'une fiche d'unité (`focusMaintenance(family)`) désigne la famille. */

/** Hypothèses d'attrition. */
const SLIDERS: SliderSpec[] = [
  { key: "dto_initial", label: k("DTO à T0"), digits: 0, unit: k("%") },
  { key: "failures_per_day", label: k("Pannes et usure par jour au contact"), digits: 1, unit: k("% du parc") },
  { key: "combat_damage_per_day", label: k("Dommages de combat par jour au contact"), digits: 1, unit: k("% du parc") },
  { key: "failures_per_100km", label: k("Pannes pour 100 km"), digits: 1, unit: k("% du parc") },
  { key: "repairs_per_day", label: k("Réparations par jour"), digits: 0, unit: k("% des matériels en panne") },
];

/** DTO attendue réglable : T0, T2 et T3 (T1 entre T0 et T2), bornes basse et haute. */
const BANDS: { phase: "T0" | "T2" | "T3"; low: keyof LogisticsAssumptions; high: keyof LogisticsAssumptions }[] = [
  { phase: "T0", low: "expected_t0_low", high: "expected_t0_high" },
  { phase: "T2", low: "expected_t2_low", high: "expected_t2_high" },
  { phase: "T3", low: "expected_t3_low", high: "expected_t3_high" },
];

export const MAINTENANCE_KEYS: readonly (keyof LogisticsAssumptions)[] = [
  ...SLIDERS.map((s) => s.key),
  ...BANDS.flatMap((b) => [b.low, b.high]),
];

const POSTURE: Record<MaintenancePosture, string> = {
  offensive: k("Engagement offensif : les unités au contact s'usent le plus."),
  defensive: k("Engagement défensif : les unités au contact s'usent un peu moins qu'en offensive."),
  mixed: k("Engagement mixte : les unités au contact s'usent un peu moins qu'en offensive."),
};

/** « T1 5 h, T2 10 h, T3 21 h ». */
const hoursText = (hours: LogisticsMaintenance["hours"]) =>
  (["T1", "T2", "T3"] as const).map((p) => t("{phase} {hours} h", { phase: p, hours: formatNumber(Math.round(hours[p])) })).join(", ");

/** Famille, courbe et lectures : le cœur de la vue, sans la requête (essais). */
export function MaintenanceBody({ data, phase }: { data: Logistics; phase: MaintenancePhase }) {
  const m = data.maintenance;
  const focus = useUi((s) => s.maintenanceFocus);
  const focusMaintenance = useUi((s) => s.focusMaintenance);
  const [chosen, setChosen] = useState<MaterielFamily | null>(() => (m ? initialFamily(m, focus) : null));

  // Famille désignée par « Ouvrir la maintenance » (constat de cohérence, fiche d'unité) : choisie, amenée à l'écran
  // et mise en surbrillance (fondu), puis oubliée. La recherche de la cible n'est annulée qu'au démontage :
  // `focusMaintenance(null)` relance l'effet aussitôt et ne doit pas l'interrompre.
  const cancel = useRef<(() => void) | null>(null);
  useEffect(() => () => cancel.current?.(), []);
  useEffect(() => {
    if (!focus || !m) return;
    if (m.families.some((f) => f.family === focus)) {
      setChosen(focus);
      cancel.current?.();
      cancel.current = designateSoon(`[data-maintenance-family="${focus}"]`, "ring");
    }
    focusMaintenance(null);
  }, [focus, m]);

  if (!m || !m.families.length) return null;
  const current = m.families.find((f) => f.family === chosen) ?? m.families[0]!;
  const band = m.expected[phase];
  const lowest = m.lowest;
  const names = new Map(data.lines.map((l) => [l.key, l.name]));
  const units = Object.entries(m.units)
    .flatMap(([key, phases]) => (phases[phase] && names.has(key) ? [{ key, name: names.get(key)!, capops: phases[phase] }] : []))
    .sort((a, b) => a.capops.capops - b.capops.capops || a.name.localeCompare(b.name));
  return (
    <>
      <PanelGroup title={t("DTO par famille de matériel · temps {phase}", { phase })}>
        <div className="space-y-2 px-4 pb-1">
          <p className="text-meta leading-snug text-ink-soft">
            <strong className="font-semibold text-ink">{t(DTO_FULL)}</strong>
            {t(" : part des matériels techniquement disponibles. Elle baisse au fil de l'engagement et se juge contre la DTO attendue de chaque temps.")}
          </p>
          <div className="grid grid-cols-2 gap-2">
            {lowest && (
              <StatTile
                icon={<DtoStatusIcon percent={lowest.dto} low={band.low} size={12} />}
                value={percentText(lowest.dto)}
                tone={colorTone(dtoColor(lowest.dto, band.low))}
                label={t("DTO la plus en retard en {phase}", { phase })}
                hint={familyLabel(lowest.family)}
              />
            )}
            <StatTile value={bandText(band)} label={t("DTO attendue en {phase}", { phase })} hint={t("Au-dessus : conforme")} />
          </div>
        </div>
        <ul className="space-y-px">
          {m.families.map((f) => {
            const value = f.dto[phase];
            return (
              <li key={f.family}>
                <PanelRow
                  data-maintenance-family={f.family}
                  icon={<DtoStatusIcon percent={value} low={band.low} />}
                  title={familyLabel(f.family)}
                  detail={
                    <>
                      {t("{n} véhicules", { n: formatNumber(Math.round(f.vehicles)) })}
                      {f.below ? (
                        <span className="text-warn"> · {t("sous la DTO attendue dès {phase}", { phase: f.below })}</span>
                      ) : (
                        <span> · {dtoReading(value, band)}</span>
                      )}
                    </>
                  }
                  state={<DtoBadge percent={value} low={band.low} />}
                  selected={f.family === current.family}
                  onClick={() => setChosen(f.family)}
                  clickTitle={t("Voir l'évolution de T0 à T3")}
                />
              </li>
            );
          })}
        </ul>
        {/* Changement de famille : la courbe et ses lignes reviennent en fondu. */}
        <Motion origin="fade" key={current.family} className="space-y-2 px-4 pt-2 pb-1">
          <p className="text-meta leading-snug font-medium text-ink-muted">
            {t("Évolution de T0 à T3 · {family}", { family: familyLabel(current.family) })}
          </p>
          <DtoCurve family={current} expected={m.expected} phase={phase} />
          <LegLines family={current} hours={m.hours} />
          {m.severity > 1.005 && (
            <p className="text-meta leading-snug text-ink-muted">
              {t("Pannes aggravées par le terrain et la météo : × {n}.", { n: formatNumber(m.severity, { maximumFractionDigits: 2 }) })}
            </p>
          )}
        </Motion>
      </PanelGroup>

      {units.length > 0 && (
        <PanelGroup title={t("CAPOPS des unités · temps {phase}", { phase })} count={units.length}>
          <p className="px-4 pb-1 text-meta leading-snug text-ink-soft">
            <strong className="font-semibold text-ink">{t(CAPOPS_FULL)}</strong>
            {t(" : la plus faible du personnel, des matériels et des munitions, en % du potentiel initial.")}
          </p>
          <ul className="space-y-px">
            {units.map((u) => {
              const limit = limitText(u.capops);
              return (
                <li key={u.key}>
                  <PanelRow
                    title={<span className="font-semibold">{unitDesignation(u.name)}</span>}
                    detail={limit ? limit.charAt(0).toUpperCase() + limit.slice(1) : t("Rien ne la limite")}
                    state={<CapopsBadge percent={u.capops.capops} />}
                  />
                </li>
              );
            })}
          </ul>
        </PanelGroup>
      )}
    </>
  );
}

/** Fourchette de DTO attendue d'un temps : deux curseurs (borne basse, borne haute). */
function BandSliders({
  data,
  item,
  disabled,
  reverts,
  onCommit,
}: {
  data: Logistics;
  item: (typeof BANDS)[number];
  disabled: boolean;
  reverts: number;
  onCommit: (changes: Partial<LogisticsAssumptions>) => void;
}) {
  const specs: SliderSpec[] = [
    { key: item.low, label: k("Borne basse"), digits: 0, unit: k("%") },
    { key: item.high, label: k("Borne haute"), digits: 0, unit: k("%") },
  ];
  return (
    <div role="group" aria-label={t("DTO attendue en {phase}", { phase: item.phase })}>
      <p className="text-meta font-medium text-ink-soft">{t("DTO attendue en {phase}", { phase: item.phase })}</p>
      <div className="mt-1 grid grid-cols-2 gap-x-4">
        {specs.map((spec) => (
          <Slider
            key={`${spec.key}-${reverts}`}
            spec={spec}
            value={data.assumptions[spec.key]}
            bounds={data.bounds[spec.key]}
            disabled={disabled}
            onCommit={(value) => onCommit({ [spec.key]: value })}
          />
        ))}
      </div>
    </div>
  );
}

export function MaintenancePanel() {
  const exerciseId = useUi((s) => s.exerciseId);
  const phase = useUi((s) => s.phase);
  const setView = useUi((s) => s.setView);
  const { query, key, opo, complete, centac } = useLogistics(exerciseId, phase);
  const { save, reverts } = useSaveAssumptions(exerciseId, phase, opo, query, key);
  const data = query.data;
  const m = data?.maintenance;
  const custom = data ? customOf(data, MAINTENANCE_KEYS) : false;

  return (
    <FloatingPanel
      enter="left"
      width="view"
      icon={VIEW_META.maintenance.icon}
      title={t("Maintenance")}
      actions={<HelpButton screen="maintenance" buttonSize="sm" size={16} />}
    >
      {!exerciseId || !complete ? (
        <Motion origin="fade">
          <EmptyState variant="inline" action={<OpenMissionDataButton />}>
            <p className="text-ink-soft">
              {t(
                "La maintenance suit, de T0 à T3, la DTO (disponibilité technique opérationnelle) de chaque famille de matériel et la CAPOPS (capacité opérationnelle) des unités du dispositif affiché sur la carte.",
              )}
            </p>
            <p className="mt-2">
              {!exerciseId ? t("Pour suivre la maintenance, créez d'abord le dossier.") : t("Complétez les Données initiales.")}
            </p>
          </EmptyState>
        </Motion>
      ) : query.isError ? (
        <div className="px-4 py-4">
          {zoneRefusalOf(query.error) ? (
            <ZoneWaitCallout message={query.error.message} error={query.error} />
          ) : (
            <Callout tone="danger">{query.error.message}</Callout>
          )}
        </div>
      ) : !data ? (
        <div className="flex items-center gap-2 px-4 py-6 text-ui text-ink-muted">
          <Spinner decorative /> {<Waiting>{t("Calcul de la maintenance…")}</Waiting>}
        </div>
      ) : centac ? (
        <Motion origin="fade" className="px-4 py-4">
          <Callout
            tone="info"
            action={
              <Button variant="secondary" size="sm" onClick={() => setView("order")}>
                <FileText size={14} /> {t("Ouvrir l'ordre")}
              </Button>
            }
          >
            {t("En rotation CENTAC, la CAPOPS vient des résultats de chaque OPO de la rotation : la maintenance n'est pas estimée.")}
          </Callout>
        </Motion>
      ) : !data.totals || !m ? (
        <Motion origin="fade">
          <p className="flex items-start gap-2 border-b border-line px-4 py-3.5 text-ui leading-relaxed text-ink-soft">
            <Swords size={14} className="mt-0.5 shrink-0 text-ink-muted" />
            {data.empty === "no_ami"
              ? t("Aucune unité amie n'est placée au temps {phase}.", { phase: data.phase })
              : t("Aucun ennemi n'est placé au temps {phase} : l'engagement des unités se mesure face au dispositif ennemi.", {
                  phase: data.phase,
                })}
          </p>
        </Motion>
      ) : (
        <Motion origin="fade" className={clsx("transition-opacity", query.isFetching && "opacity-80")}>
          <PanelGroup title={t("En direct depuis la carte · temps {phase}", { phase: data.phase })} flush>
            <div className="space-y-1.5 px-4 pb-1">
              <SituationLine icon={<Clock size={12} />}>
                {t("Durée des temps d'après les GDH du dossier : {hours}.", { hours: hoursText(m.hours) })}
              </SituationLine>
              <SituationLine icon={<Swords size={12} />}>{t(POSTURE[m.posture])}</SituationLine>
              <SituationLine icon={<Mountain size={12} />}>{mobilityLabel(data.situation)}</SituationLine>
              <SituationLine icon={<Wrench size={12} />}>{expectedText(data.phase, m.expected[data.phase])}</SituationLine>
            </div>
          </PanelGroup>

          <MaintenanceBody data={data} phase={data.phase} />

          <PanelGroup flush>
            <PanelRow
              icon={<Fuel size={14} />}
              title={t("Logistique")}
              detail={t("Carburant, munitions et drones : besoins estimés dans leur vue.")}
              state={<ChevronRight size={14} className="text-ink-muted" aria-hidden />}
              onClick={() => setView("logistics")}
              clickTitle={t("Ouvrir la logistique")}
            />
          </PanelGroup>

          <div>
            <Collapsible title={t("Définitions")}>
              <div className="space-y-2 text-justify text-meta leading-relaxed text-ink-soft hyphens-auto">
                <p>
                  <strong className="font-semibold text-ink">{t(DTO_FULL)}</strong>
                  {t(
                    " : part des matériels techniquement disponibles (en état de rouler et de combattre), suivie par famille de matériel et par unité. Elle part de la DTO à T0 et baisse à chaque temps, d'autant plus que le temps dure et que l'unité est engagée : pannes et usure, plus nombreuses quand le terrain et la météo gênent le mouvement, pannes du mouvement, et au contact dommages de combat, dont une part détruit le matériel. Les réparations en rattrapent une part ; les matériels détruits ne reviennent pas. La DTO d'une famille est la moyenne de ses unités, pondérée par leurs véhicules.",
                  )}
                </p>
                <p>
                  <strong className="font-semibold text-ink">{t("DTO attendue")}</strong>
                  {t(
                    " : fourchette de chaque temps, que suit une force engagée plusieurs jours en haute intensité (par défaut 80 à 90 % en T0, 70 à 82 % en T1, 60 à 75 % en T2, 55 à 65 % en T3). Une DTO dans la fourchette ou au-dessus est conforme, jamais une alerte : une opération courte ou une unité en réserve reste au-dessus. Sous la borne basse : orange de moins de 5 points, rouge au-delà.",
                  )}
                </p>
                <p>
                  <strong className="font-semibold text-ink">{t(CAPOPS_FULL)}</strong>
                  {t(
                    " : aptitude d'une unité à remplir sa mission, en pourcentage de son potentiel initial : la plus faible de trois composantes, le personnel (potentiel de l'unité), les matériels (DTO de l'unité rapportée à sa DTO de T0) et les munitions (d'après les UF consommées). Vert au-dessus de 80 %, orange de 61 à 80 %, rouge à 60 % ou moins. Une unité qui suit la DTO attendue garde ainsi une CAPOPS orange au plus en T3.",
                  )}
                </p>
              </div>
            </Collapsible>
            <Collapsible title={t("Hypothèses (réglables)")} trailing={<AssumptionsBadge data={data} custom={custom} />}>
              <div className="space-y-3">
                {SLIDERS.map((spec) => (
                  <Slider
                    key={`${spec.key}-${reverts}`}
                    spec={spec}
                    value={data.assumptions[spec.key]}
                    bounds={data.bounds[spec.key]}
                    disabled={save.isPending}
                    onCommit={(value) => save.mutate({ [spec.key]: value })}
                  />
                ))}
                {BANDS.map((item) => (
                  <BandSliders key={item.phase} data={data} item={item} disabled={save.isPending} reverts={reverts} onCommit={(c) => save.mutate(c)} />
                ))}
                <p className="text-meta leading-snug text-ink-muted">{t("La DTO attendue en T1 se place entre celles de T0 et de T2.")}</p>
                <ResetAssumptions data={data} keys={MAINTENANCE_KEYS} custom={custom} pending={save.isPending} onReset={(v) => save.mutate(v)} />
              </div>
            </Collapsible>
          </div>

          <p className="border-t border-line px-4 py-3 text-justify text-meta leading-relaxed text-ink-muted hyphens-auto">
            {t(
              "Prévision de planification : les taux et les fourchettes sont des ordres de grandeur réglables, pas des valeurs réglementaires. Les pannes et destructions jouées dans la DIREX s'y ajoutent.",
            )}
          </p>
        </Motion>
      )}
    </FloatingPanel>
  );
}
